import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(email, password);
      // La redirección se maneja automáticamente en App.tsx
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}
      >
        {/* Logo */}
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <img 
            src="/logo_inicio.png" 
            alt="Logo" 
            style={{ 
              maxWidth: '70%',
              height: 'auto',
              display: 'block',
              margin: '0 auto'
            }} 
          />
        </Box>

        {/* Formulario minimalista */}
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                color: '#ff6b6b',
                border: '1px solid rgba(211, 47, 47, 0.3)'
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            required
            fullWidth
            id="email"
            placeholder="Correo Electrónico"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#FFEB5D',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FFEB5D',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1
              }
            }}
          />
          
          <TextField
            required
            fullWidth
            name="password"
            placeholder="Contraseña"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#FFEB5D',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FFEB5D',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1
              }
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.5,
              backgroundColor: '#FFEB5D',
              color: '#000000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#FFE135',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 235, 93, 0.3)',
                color: 'rgba(0, 0, 0, 0.5)'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#000000' }} />
            ) : (
              'Iniciar Sesión'
            )}
          </Button>
        </Box>

        {/* Indicación minimalista */}
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.75rem',
            textAlign: 'center'
          }}
        >
          💡 Solicita tus credenciales al administrador del sistema
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;